import React, { useContext, useCallback, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { IconButton, InputBase, Tooltip } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { storeContext } from '../../provider/Provider';
import { premiumBacklinkActions } from '../../../store/premium-backlink-reducer';
import useStyles from '../styles/PremiumBacklink.style';
import { 
  premiumBacklinksBacklinksList, 
  premiumBacklinksOrdersList, 
  premiumBacklinksOrdersCreate,
  premiumBacklinksClientProfilesList 
} from '../services/premium-backlink.service';

export const CART_STORAGE_KEY = 'premiumBacklink_cart';

// Helper function to get client-specific cart key
const getClientCartKey = (clientDomain) => `premiumBacklink_cart_${clientDomain}`;

const usePremiumBacklink = () => {
  const { state, dispatch } = useContext(storeContext);
  const { premiumBacklinks, websiteSwitch } = state;
  const classes = useStyles();
  
  // Ref to store the current toast timer
  const toastTimerRef = useRef(null);

  // Get state values from store
  const {
    cart,
    modalOpen,
    termsModalOpen,
    snackbarOpen,
    snackbarMessage,
    customToastOpen,
    customToastMessage,
    isFetchingBacklinks,
    isFetchingOrders,
    isCreatingOrder,
    error,
    orderError,
    backlinks,
    orders,
    clientProfiles,
    pagination,
    filters,
    sorting,
  } = premiumBacklinks;

  const loadPersistedCart = useCallback(() => {
    try {
      const clientDomain = websiteSwitch.currentWebsite;
      if (!clientDomain) return [];
      
      const clientCartKey = getClientCartKey(clientDomain);
      const saved = localStorage.getItem(clientCartKey);
      return saved ? JSON.parse(saved) : [];
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error loading persisted cart:', err);
      return [];
    }
  }, [websiteSwitch.currentWebsite]);

  const saveCart = useCallback((cartData) => {
    try {
      const clientDomain = websiteSwitch.currentWebsite;
      if (!clientDomain) return;
      
      const clientCartKey = getClientCartKey(clientDomain);
      localStorage.setItem(clientCartKey, JSON.stringify(cartData));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error saving cart to localStorage:', err);
    }
  }, [websiteSwitch.currentWebsite]);

  const clearClientCart = useCallback(() => {
    try {
      const clientDomain = websiteSwitch.currentWebsite;
      if (!clientDomain) return;
      
      const clientCartKey = getClientCartKey(clientDomain);
      localStorage.removeItem(clientCartKey);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error clearing client cart from localStorage:', err);
    }
  }, [websiteSwitch.currentWebsite]);

  const restoreClientCart = useCallback(() => {
    try {
      const clientDomain = websiteSwitch.currentWebsite;
      if (!clientDomain) return;
      
      const clientCartKey = getClientCartKey(clientDomain);
      const savedCart = localStorage.getItem(clientCartKey);
      
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        dispatch({
          type: premiumBacklinkActions.SET_CART,
          payload: { cart: parsedCart },
        });
      } else {
        dispatch({
          type: premiumBacklinkActions.SET_CART,
          payload: { cart: [] },
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error restoring client cart:', err);
      dispatch({
        type: premiumBacklinkActions.SET_CART,
        payload: { cart: [] },
      });
    }
  }, [websiteSwitch.currentWebsite, dispatch]);

  useEffect(() => {
    if (websiteSwitch.currentWebsite) {
      restoreClientCart();
    }
  }, [websiteSwitch.currentWebsite, restoreClientCart]);


  // Calculate cart totals
  const calculateCartCount = (cartData) => cartData.reduce((sum, item) => sum + item.quantity, 0);

  const calculateCartSubtotal = (cartData) => cartData.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Get available backlinks for purchase
  const getAvailableBacklinks = useMemo(() => backlinks.filter(item => 
    item.status === 'available' || 
    item.status === 'to_be_purchased' ||
    !item.status // fallback for items without status
  ), [backlinks]);

  const clientId = useMemo(() => {
    const clientProfile = clientProfiles.find(profile => 
      profile.client_domain === websiteSwitch.currentWebsite
    );
    return clientProfile ? clientProfile.client : null;
  }, [clientProfiles, websiteSwitch.currentWebsite]);



  // API Functions
  const fetchPremiumBacklinks = useCallback(async (params = {}) => {
    dispatch({
      type: premiumBacklinkActions.SET_IS_FETCHING_BACKLINKS,
      payload: { isFetchingBacklinks: true },
    });
    
    try {
      // Build filter parameters
      const filterParams = {};
      
      // Add text filters
      if (filters.name) filterParams.name = filters.name;
      if (filters.domain) filterParams.domain = filters.domain;
      if (filters.rel) filterParams.rel = filters.rel;
      if (filters.turn_around) filterParams.turn_around = filters.turn_around;
      
      // Add exact value filters
      if (filters.dr) filterParams.dr = filters.dr;
      if (filters.ok) filterParams.ok = filters.ok;
      if (filters.ot) filterParams.ot = filters.ot;
      if (filters.price) filterParams.price = filters.price;
      
      // Add sorting parameter
      let orderingParam = '';
      if (sorting.orderBy) {
        orderingParam = sorting.order === 'desc' ? `-${sorting.orderBy}` : sorting.orderBy;
      }
      
      // Add pagination parameters
      // Note: Using large page_size to fetch all records (pagination UI is hidden)
      const apiParams = {
        page: 1, // Always fetch first page
        page_size: 10000, // Large page size to get all records
        ...filterParams,
        ...params
      };
      
      // Add ordering parameter if it exists
      if (orderingParam) {
        apiParams.ordering = orderingParam;
      }
      
      const response = await premiumBacklinksBacklinksList(apiParams);
      
      // Handle paginated response
      const { data } = response;
      const results = data.results || data;
      const count = data.count || results.length;
      
      dispatch({
        type: premiumBacklinkActions.SET_BACKLINKS,
        payload: { backlinks: results },
      });
      
      // Update pagination info
      dispatch({
        type: premiumBacklinkActions.SET_PAGINATION,
        payload: {
          total: count,
          totalPages: Math.ceil(count / pagination.perPage),
        },
      });
      
      return response.data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error fetching premium backlinks:', err);
      throw err;
    } finally {
      dispatch({
        type: premiumBacklinkActions.SET_IS_FETCHING_BACKLINKS,
        payload: { isFetchingBacklinks: false },
      });
    }
  }, [dispatch, pagination.currentPage, pagination.perPage, filters, sorting]);

  const fetchPremiumBacklinkOrders = useCallback(async (params = {}) => {
    // Clear existing data first
    dispatch({
      type: premiumBacklinkActions.SET_ORDERS,
      payload: { orders: [] },
    });
    
    dispatch({
      type: premiumBacklinkActions.SET_IS_FETCHING_ORDERS,
      payload: { isFetchingOrders: true },
    });
    
    try {
      if (!clientId) {
          throw new Error('Client ID not found for current website');
      }
      
      // Add client ID as filter
      const apiParams = {
        ...params,
        client: clientId,
      };
      
      const response = await premiumBacklinksOrdersList(apiParams);
      
      dispatch({
        type: premiumBacklinkActions.SET_ORDERS,
        payload: { orders: response.data.results || response.data },
      });
      
      return response.data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error fetching premium backlink orders:', err);
      throw err;
    } finally {
      dispatch({
        type: premiumBacklinkActions.SET_IS_FETCHING_ORDERS,
        payload: { isFetchingOrders: false },
      });
    }
  }, [dispatch, clientId]);

  const createPremiumBacklinkOrder = useCallback(async (orderData) => {
    dispatch({
      type: premiumBacklinkActions.SET_IS_CREATING_ORDER,
      payload: { isCreatingOrder: true },
    });
    
    // Clear previous errors
    dispatch({
      type: premiumBacklinkActions.SET_ORDER_ERROR,
      payload: { orderError: null },
    });
    
    try {
      const response = await premiumBacklinksOrdersCreate(orderData);
      
      // Refresh orders after creating new one using the same client ID
      try {
        // Small delay to ensure API has processed the order
        setTimeout(async () => {
          try {
            await fetchPremiumBacklinkOrders({ client: orderData.client });
          } catch (refreshError) {
            // eslint-disable-next-line no-console
            console.warn('Failed to refresh orders after creation:', refreshError);
          }
        }, 500);
      } catch (refreshError) {
        // eslint-disable-next-line no-console
        console.warn('Failed to refresh orders after creation:', refreshError);
      }
      
      return response.data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error creating premium backlink order:', err);
      
      dispatch({
        type: premiumBacklinkActions.SET_ORDER_ERROR,
        payload: { orderError: err.response?.data?.message || err.message || 'Failed to create order' },
      });
      
      throw err;
    } finally {
      dispatch({
        type: premiumBacklinkActions.SET_IS_CREATING_ORDER,
        payload: { isCreatingOrder: false },
      });
    }
  }, [dispatch, fetchPremiumBacklinkOrders]);

  const fetchPremiumBacklinkClientProfiles = useCallback(async () => {
    try {
      const response = await premiumBacklinksClientProfilesList();
      
      dispatch({
        type: premiumBacklinkActions.SET_CLIENT_PROFILES,
        payload: { clientProfiles: response.data.results || response.data },
      });
      
      return response.data;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('Error fetching premium backlink client profiles:', err);
      throw err;
    }
  }, [dispatch]);



    // Helper function to get turn-around chip class based on turnaround value
    const getTurnaroundChipClass = useCallback((turnaround) => {
      if (!turnaround || typeof turnaround !== 'string') {
        return `${classes.turnaroundChip} ${classes.turnaroundChipLong}`; // Default purple
      }
      
      const lowerTurnaround = turnaround.toLowerCase();
      
      // Fast (days) - orange styling
      if (lowerTurnaround.includes('fast') || lowerTurnaround.includes('days')) {
        return `${classes.turnaroundChip} ${classes.turnaroundChipShort}`;
      }
      
      // Medium, Extremely slow, or other cases - purple styling
      return `${classes.turnaroundChip} ${classes.turnaroundChipLong}`;
    }, [classes.turnaroundChip, classes.turnaroundChipShort, classes.turnaroundChipLong]);

    // Helper function to get status styling for chips
    const getStatusStyle = useCallback((status) => {
      switch (status) {
        // Order statuses
        case 'complete':
        case 'completed':
          return { backgroundColor: '#e8f5e9', color: '#00c853' };
        case 'in_progress':
          return { backgroundColor: '#fff3e0', color: '#ff6b00' };
        
        // Backlink statuses
        case 'live':
          return { backgroundColor: '#e3f2fd', color: '#1976d2' };
        case 'listed':
          return { backgroundColor: '#e8f5e9', color: '#00c853' };
        case 'published':
          return { backgroundColor: '#f3e5f5', color: '#7b1fa2' };
        case 'ready_to_send':
          return { backgroundColor: '#fff8e1', color: '#f57c00' };
        case 'publish_pending':
          return { backgroundColor: '#fce4ec', color: '#c2185b' };
        case 'need_article':
          return { backgroundColor: '#f3e5f5', color: '#7b1fa2' };
        case 'anchor_and_target':
          return { backgroundColor: '#e0f2f1', color: '#00695c' };
        case 'returned_for_updates':
          return { backgroundColor: '#ffebee', color: '#d32f2f' };
        case 'not_archived':
          return { backgroundColor: '#f5f5f5', color: '#757575' };
        
        default:
          return status ? { backgroundColor: '#fff3e0', color: '#ff6b00' } : {};
      }
    }, []);

    // Helper function to get display-friendly status labels
    const getStatusLabel = useCallback((status) => {
      switch (status) {
        // Order statuses
        case 'complete':
        case 'completed':
          return 'Complete';
        case 'in_progress':
          return 'In Progress';
        
        // Backlink statuses
        case 'live':
          return 'Live';
        case 'listed':
          return 'Listed';
        case 'published':
          return 'Published';
        case 'ready_to_send':
          return 'Ready to Send';
        case 'publish_pending':
          return 'Publish Pending';
        case 'need_article':
          return 'Need Article';
        case 'anchor_and_target':
          return 'Anchor & Target';
        case 'returned_for_updates':
          return 'Returned for Updates';
        case 'not_archived':
          return 'Not Archived';
        
        default:
          return status || '';
      }
    }, []);

  // Component functions

  // Simplified cart management - no more quantities state
  const handleAddToCart = useCallback((itemKey, quantity) => {
    if (quantity <= 0) {
      dispatch({
        type: premiumBacklinkActions.SET_SNACKBAR,
        payload: {
          snackbarOpen: true,
          snackbarMessage: 'Please set quantity greater than 0 before adding to cart.',
        },
      });
      return;
    }

    // Find the specific item to add
    const itemToAdd = getAvailableBacklinks.find(row => row.id === itemKey);
    if (!itemToAdd) return;

    // Check if item already exists in cart
    const existingCartItemIndex = cart.findIndex(item => item.id === itemKey);
    let newCart;
    
    if (existingCartItemIndex >= 0) {
      // Update existing item quantity to the specific number
      newCart = [...cart];
      newCart[existingCartItemIndex] = { ...newCart[existingCartItemIndex], quantity };
    } else {
      // Add new item to cart with specific quantity
      newCart = [...cart, { ...itemToAdd, quantity }];
    }
    
    // Update cart in the store
    dispatch({
      type: premiumBacklinkActions.SET_CART,
      payload: { cart: newCart },
    });

    saveCart(newCart);

    // Show success toast
    dispatch({
      type: premiumBacklinkActions.SET_CUSTOM_TOAST,
      payload: {
        customToastOpen: true,
        customToastMessage: `${itemToAdd.name} successfully added to cart.`,
      },
    });
    
    // Auto-hide after 5 seconds
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
    }
    toastTimerRef.current = setTimeout(() => {
      dispatch({
        type: premiumBacklinkActions.CLOSE_CUSTOM_TOAST,
      });
      toastTimerRef.current = null;
    }, 5000);
  }, [cart, getAvailableBacklinks, dispatch]);

  // Purchase cell component with local state
  const PurchaseCell = React.memo(({ row, initialQuantity, onAddToCart, purchaseClasses }) => {
    const [localQuantity, setLocalQuantity] = React.useState(initialQuantity);

    // Sync local quantity when cart changes (from deletes, updates, etc.)
    React.useEffect(() => {
      setLocalQuantity(initialQuantity);
    }, [initialQuantity]);

    const handleLocalQuantityChange = (delta) => {
      setLocalQuantity(prev => Math.max(0, prev + delta));
    };

    const handleLocalQuantitySet = (value) => {
      setLocalQuantity(Math.max(0, parseInt(value, 10) || 0));
    };

    const handleAddToCartClick = () => {
      onAddToCart(row.id, localQuantity);
    };

    return (
      <div className={purchaseClasses.purchaseCell}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <IconButton
            className={purchaseClasses.qtyButton}
            onClick={() => handleLocalQuantityChange(-1)}
            disabled={localQuantity <= 0} 
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <InputBase
            className={purchaseClasses.qtyInput}
            value={localQuantity}
            onChange={(e) => handleLocalQuantitySet(e.target.value)}
            inputProps={{ readOnly: false }}
          />
          <IconButton
            className={purchaseClasses.qtyButton}
            onClick={() => handleLocalQuantityChange(1)}
          >
            <AddIcon fontSize="small" />
          </IconButton>
          <button
            type="button"
            className={purchaseClasses.addToCartButton}
            onClick={handleAddToCartClick}
          >
            Add to Cart
          </button>
        </div>
      </div>
    );
  });

  // PropTypes for PurchaseCell
  PurchaseCell.propTypes = {
    row: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    }).isRequired,
    initialQuantity: PropTypes.number.isRequired,
    onAddToCart: PropTypes.func.isRequired,
    purchaseClasses: PropTypes.shape({
      purchaseCell: PropTypes.string,
      qtyButton: PropTypes.string,
      qtyInput: PropTypes.string,
      addToCartButton: PropTypes.string,
    }).isRequired,
  };

  const createPurchaseCell = useCallback((row) => {
    const currentCartItem = cart.find(item => item.id === row.id);
    const initialQuantity = currentCartItem ? currentCartItem.quantity : 1;
    
    return (
      <PurchaseCell 
        row={row} 
        initialQuantity={initialQuantity}
        onAddToCart={handleAddToCart}
        purchaseClasses={classes}
      />
    );
  }, [cart, classes, handleAddToCart]);



  const updateCartItemQuantity = useCallback((itemKey, newQuantity) => {
    if (newQuantity <= 0) {
      // Remove item from cart if quantity is 0 or less
      const newCart = cart.filter(item => item.id !== itemKey);
      dispatch({
        type: premiumBacklinkActions.SET_CART,
        payload: { cart: newCart },
      });
      saveCart(newCart);
    } else {
      const newCart = cart.map(item => 
        item.id === itemKey ? { ...item, quantity: newQuantity } : item
      );
      dispatch({
        type: premiumBacklinkActions.SET_CART,
        payload: { cart: newCart },
      });
      saveCart(newCart);
    }
  }, [cart, dispatch, saveCart]);

  const removeFromCart = useCallback((itemKey) => {
    // Set quantity to 0 to remove the item
    updateCartItemQuantity(itemKey, 0);
  }, [updateCartItemQuantity]);

  const confirmOrder = useCallback(async () => {
    if (cart.length === 0) {
      dispatch({
        type: premiumBacklinkActions.SET_SNACKBAR,
        payload: {
          snackbarOpen: true,
          snackbarMessage: 'Cart is empty. Please add items to cart before ordering.',
        },
      });
      return;
    }
    
    try {
      if (!clientId) {
        throw new Error(`Client ID not found for current website: ${websiteSwitch.currentWebsite}`);
      }
      
      // Create order with cart items
      const orderData = {
        client: clientId,
        order_items: cart.map(item => ({
          backlink: item.id,
          quantity: item.quantity,
          price_per_item: item.price,
        }))
      };
      
      await createPremiumBacklinkOrder(orderData);
      
      // Clear cart after successful order
      dispatch({
        type: premiumBacklinkActions.CLEAR_CART,
      });
      
      clearClientCart();
      
      dispatch({
        type: premiumBacklinkActions.SET_MODAL_OPEN,
        payload: { modalOpen: false },
      });
      
      dispatch({
        type: premiumBacklinkActions.SET_SNACKBAR,
        payload: {
          snackbarOpen: true,
          snackbarMessage: 'Order created successfully!',
        },
      });
      
    } catch (err) {
      dispatch({
        type: premiumBacklinkActions.SET_SNACKBAR,
        payload: {
          snackbarOpen: true,
          snackbarMessage: err.message || 'Failed to create order. Please try again.',
        },
      });
    }
  }, [cart, clientId, websiteSwitch.currentWebsite, createPremiumBacklinkOrder, backlinks, dispatch]);

  const openModal = () => {
    dispatch({
      type: premiumBacklinkActions.SET_MODAL_OPEN,
      payload: { modalOpen: true },
    });
  };

  const closeModal = () => {
    dispatch({
      type: premiumBacklinkActions.SET_MODAL_OPEN,
      payload: { modalOpen: false },
    });
  };

  const openTermsModal = () => {
    dispatch({
      type: premiumBacklinkActions.SET_TERMS_MODAL_OPEN,
      payload: { termsModalOpen: true },
    });
  };

  const closeTermsModal = () => {
    dispatch({
      type: premiumBacklinkActions.SET_TERMS_MODAL_OPEN,
      payload: { termsModalOpen: false },
    });
  };

  const handleCloseSnackbar = () => {
    dispatch({
      type: premiumBacklinkActions.CLOSE_SNACKBAR,
    });
  };

  const handleCloseCustomToast = () => {
    // Clear any existing timer
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    
    dispatch({
      type: premiumBacklinkActions.CLOSE_CUSTOM_TOAST,
    });
  };

  // Pagination functions
  const handleChangePage = useCallback((event, newPage) => {
    dispatch({
      type: premiumBacklinkActions.SET_CURRENT_PAGE,
      payload: { currentPage: newPage },
    });
  }, [dispatch]);

  const handleChangeRowsPerPage = useCallback((event) => {
    const newPerPage = parseInt(event.target.value, 10);
    dispatch({
      type: premiumBacklinkActions.SET_PER_PAGE,
      payload: { perPage: newPerPage },
    });
  }, [dispatch]);

  // Filter functions
  const handleFilterChange = useCallback((newFilters) => {
    dispatch({
      type: premiumBacklinkActions.SET_FILTERS,
      payload: { filters: newFilters },
    });
  }, [dispatch]);

  const clearFilters = useCallback(() => {
    dispatch({
      type: premiumBacklinkActions.CLEAR_FILTERS,
    });
  }, [dispatch]);

  // Sorting functions
  const handleSortChange = useCallback((columnId) => {
    let newOrderBy = columnId;
    let newOrder = 'asc';
    
    if (sorting.orderBy === columnId) {
      if (sorting.order === 'asc') {
        newOrder = 'desc';
      } else if (sorting.order === 'desc') {
        // Third click: clear sorting
        newOrderBy = '';
        newOrder = 'asc';
      }
    }
    
    dispatch({
      type: premiumBacklinkActions.SET_SORTING,
      payload: { 
        sorting: { 
          orderBy: newOrderBy, 
          order: newOrder 
        } 
      },
    });
  }, [dispatch, sorting.orderBy, sorting.order]);


  const columns = [
    {
      id: 'name',
      label: 'Name',
      align: 'left',
      width: '120px',
      filter: {
        type: 'text',
        placeholder: 'Name',
        fullWidth: true,
        style: { minWidth: '80px' }
      },
      render: (row) => (
        <div 
          style={{ 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}
          title={row.name || '-'}
        >
          {row.name || '-'}
        </div>
      )
    },
    {
      id: 'domain',
      label: 'Domain',
      align: 'left',
      width: '8%',
      filter: {
        type: 'text',
        placeholder: 'Domain',
        fullWidth: true,
        style: { minWidth: '90px' }
      },
      render: (row) => (
        <a href={`https://${row.domain || ''}`} target="_blank" rel="noopener noreferrer" className={classes.link}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {row.domain || '-'}
          </div>
        </a>
      )
    },
    {
      id: 'dr',
      label: (
        <Tooltip 
          title="Domain Rating (DR), created by Ahrefs, shows the strength of a website's backlink profile on a 100-point scale. Learn more at https://help.ahrefs.com/en/articles/1409408-what-is-domain-rating-dr" 
          arrow
          classes={{ tooltip: classes.chakraTooltip, arrow: classes.chakraTooltipArrow }}
        >
          <span style={{ cursor: 'help' }}>DR</span>
        </Tooltip>
      ),
      align: 'right',
      width: '8%',
      render: (row) => row.dr || '-'
    },
    {
      id: 'ok',
      label: (
        <Tooltip 
          title="Organic Keywords. Ahrefs estimated number of organic keywords a domain rankings for." 
          arrow
          classes={{ tooltip: classes.chakraTooltip, arrow: classes.chakraTooltipArrow }}
        >
          <span style={{ cursor: 'help' }}>OK</span>
        </Tooltip>
      ),
      align: 'right',
      width: '80px',
      render: (row) => row.ok ? row.ok.toLocaleString() : '-'
    },
    {
      id: 'ot',
      label: (
        <Tooltip 
          title="Organic Traffic. Ahrefs estimated monthly organic visitors to a domain." 
          arrow
          classes={{ tooltip: classes.chakraTooltip, arrow: classes.chakraTooltipArrow }}
        >
          <span style={{ cursor: 'help' }}>OT</span>
        </Tooltip>
      ),
      align: 'right',
      width: '80px',
      render: (row) => row.ot ? row.ot.toLocaleString() : '-'
    },
    {
      id: 'price',
      label: 'Price',
      align: 'right',
      width: '8%',
      render: (row) => row.price ? `$${row.price.toLocaleString()}` : '-'
    },
    {
      id: 'rel',
      label: (
        <Tooltip 
          title="The link type provided, either follow, no follow, or sponsored. Generally speaking, Follow links are the best for SEO, Sponsored links provide both SEO and Brand benefit, and No Follow links provide mostly Brand benefit." 
          arrow
          classes={{ tooltip: classes.chakraTooltip, arrow: classes.chakraTooltipArrow }}
        >
          <span style={{ cursor: 'help' }}>Rel=</span>
        </Tooltip>
      ),
      align: 'left',
      width: '6%',
      render: (row) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.rel || '-'}
        </div>
      )
    },
    {
      id: 'turn_around',
      label: (
        <Tooltip 
          title="Estimated time of publication, generally calculated from the delivery of completed content to a Publisher's queue. Each Publisher has different polices regarding their queue and content, so turnaround time may vary." 
          arrow
          classes={{ tooltip: classes.chakraTooltip, arrow: classes.chakraTooltipArrow }}
        >
          <span style={{ cursor: 'help' }}>Turn-Around</span>
        </Tooltip>
      ),
      align: 'left',
      width: '8%',
      render: (row) => (
        <span 
          className={getTurnaroundChipClass(row.turn_around)}
          title={row.turn_around || '-'}
        >
          {row.turn_around || '-'}
        </span>
      )
    },
    {
      id: 'purchase',
      label: 'Purchase',
      align: 'left',
      width: '16%',
      filter: null, // No filter for purchase column
      render: (row) => createPurchaseCell(row)
    }
  ];


  const ordersInProgressCount = useMemo(() => {
    if (!orders) return 0;
    return orders.filter(order => order.status !== 'complete').length;
  }, [orders]);

  return {
    // Data
    backlinks,
    orders,
    ordersInProgressCount,
    clientProfiles,
    availableBacklinks: getAvailableBacklinks,
    clientId,
    
    // Store state
    cart,
    modalOpen,
    termsModalOpen,
    snackbarOpen,
    snackbarMessage,
    customToastOpen,
    customToastMessage,
    isFetching: isFetchingBacklinks || isFetchingOrders,
    isFetchingBacklinks,
    isFetchingOrders,
    isCreatingOrder,
    error,
    orderError,
    
    // Pagination
    pagination,
    
    // Filters
    filters,
    
    // Sorting
    sorting,
    
    // API functions
    fetchPremiumBacklinks,
    fetchPremiumBacklinkOrders,
    createPremiumBacklinkOrder,
    fetchPremiumBacklinkClientProfiles,
    
    // Component functions
    handleAddToCart,
    updateCartItemQuantity,
    removeFromCart,
    confirmOrder,
    openModal,
    closeModal,
    openTermsModal,
    closeTermsModal,
    handleCloseSnackbar,
    handleCloseCustomToast,
    
    // Pagination functions
    handleChangePage,
    handleChangeRowsPerPage,
    
    // Filter functions
    handleFilterChange,
    clearFilters,
    handleSortChange,
    
    // Utility functions
    loadPersistedCart,
    saveCart,
    clearClientCart,
    restoreClientCart,
    calculateCartCount,
    calculateCartSubtotal,
    getTurnaroundChipClass,
    getStatusStyle,
    getStatusLabel,
    getAvailableBacklinks,
    columns,
    createPurchaseCell
  };
};

export default usePremiumBacklink;